'use client';

import React, { useEffect, useRef } from 'react';

const testimonials = [
  {
    quote:
      "Didn't expect a trek to change how I think. The Hampta Pass route broke me down and built me back up in the best way possible.",
    name: 'Rahul Sharma',
    location: 'Bengaluru',
    trek: 'Hampta Pass, 2025',
    initials: 'RS',
    rating: 5,
  },
  {
    quote:
      "Perfect balance of adventure and comfort. The guides knew the mountain like it was their backyard — because it is. Never felt safer.",
    name: 'Sneha Reddy',
    location: 'Hyderabad',
    trek: 'Kedarkantha, 2025',
    initials: 'SR',
    rating: 5,
  },
  {
    quote:
      "I came for the trek. I stayed for the silence. Three weeks later I still think about that morning at 4,200m.",
    name: 'Arjun Mehta',
    location: 'Mumbai',
    trek: 'Chopta Tungnath, 2025',
    initials: 'AM',
    rating: 5,
  },
];

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const quoteRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    let ctx: { revert: () => void } | undefined;

    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        // ── HEADER ────────────────────────────────────────────────────
        gsap.fromTo(
          headerRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
          }
        );

        // ── CARDS — staggered scrub cascade ──────────────────────────
        const cardTl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 55%',
            end: 'bottom 75%',
            scrub: 1.2,
          },
        });

        cardRefs.current.forEach((card, i) => {
          if (!card) return;
          cardTl.fromTo(
            card,
            { opacity: 0, y: 70, scale: 0.92, rotateX: 8 },
            { opacity: 1, y: 0, scale: 1, rotateX: 0, duration: 0.5 },
            i * 0.2
          );
        });

        // ── QUOTE TEXT — character-level opacity ─────────────────────
        quoteRefs.current.forEach((quote) => {
          if (!quote) return;
          gsap.fromTo(
            quote,
            { opacity: 0.3 },
            {
              opacity: 1,
              duration: 0.8,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: quote,
                start: 'top 85%',
              },
            }
          );
        });
      }, sectionRef);
    };

    init();
    return () => ctx?.revert();
  }, []);

  return (
    <section data-component-id="src/app/components/TestimonialsSection.tsx:111:4" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="111" data-component-end-line="166" data-component-file="TestimonialsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-32%20md%3Apy-40%20bg-secondary%20overflow-hidden%22%7D"
      ref={sectionRef}
      className="py-32 md:py-40 bg-secondary overflow-hidden"
      aria-label="Testimonials"
    >
      <div data-component-id="src/app/components/TestimonialsSection.tsx:116:6" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="116" data-component-end-line="165" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%22%7D" className="max-w-7xl mx-auto px-6">
        <div data-component-id="src/app/components/TestimonialsSection.tsx:117:8" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="117" data-component-end-line="122" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" ref={headerRef} className="text-center mb-16" style={{ opacity: 0 }}>
          <span data-component-id="src/app/components/TestimonialsSection.tsx:118:10" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="118" data-component-end-line="118" data-component-file="TestimonialsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22section-tag%20block%20mb-4%22%2C%22textContent%22%3A%22Stories%22%7D" className="section-tag block mb-4">Stories</span>
          <h2 data-component-id="src/app/components/TestimonialsSection.tsx:119:10" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="119" data-component-end-line="121" data-component-file="TestimonialsSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20lg%3Atext-6xl%20font-bold%20tracking-tightest%20text-foreground%20leading-none%22%2C%22textContent%22%3A%22What%20the%20Trail%20Taught%20Them%22%7D" className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tightest text-foreground leading-none">
            What the Trail Taught Them
          </h2>
        </div>

        <div data-component-id="src/app/components/TestimonialsSection.tsx:124:8" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="124" data-component-end-line="164" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ perspective: '1200px' }}>
          {testimonials.map((t, i) => (
            <div data-component-id="src/app/components/TestimonialsSection.tsx:126:12" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="126" data-component-end-line="162" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-2xl%20p-8%20border%20border-border%20hover%3Ashadow-xl%20hover%3A-translate-y-2%20transition-all%20duration-400%22%7D"
              key={t.name}
              ref={(el) => { cardRefs.current[i] = el; }}
              className="bg-card rounded-2xl p-8 border border-border hover:shadow-xl hover:-translate-y-2 transition-all duration-400"
              style={{ opacity: 0, transformStyle: 'preserve-3d' }}
            >
              {/* Stars */}
              <div data-component-id="src/app/components/TestimonialsSection.tsx:133:14" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="133" data-component-end-line="139" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-1%20mb-6%22%7D" className="flex gap-1 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg data-component-id="src/app/components/TestimonialsSection.tsx:135:18" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="135" data-component-end-line="137" data-component-file="TestimonialsSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" key={star} width="14" height="14" viewBox="0 0 14 14" fill="var(--accent)" aria-hidden="true">
                    <path data-component-id="src/app/components/TestimonialsSection.tsx:136:20" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="136" data-component-end-line="136" data-component-file="TestimonialsSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M7 1l1.5 4h4.5l-3.5 2.5 1.5 4L7 9l-4 2.5 1.5-4L1 5h4.5z" />
                  </svg>
                ))}
              </div>

              {/* Quote */}
              <blockquote data-component-id="src/app/components/TestimonialsSection.tsx:142:14" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="142" data-component-end-line="148" data-component-file="TestimonialsSection.tsx" data-component-name="blockquote" data-component-content="%7B%22elementName%22%3A%22blockquote%22%2C%22className%22%3A%22text-foreground%20text-base%20leading-relaxed%20mb-8%20font-medium%22%2C%22textContent%22%3A%22%5C%22%20%5C%22%22%7D"
                ref={(el) => { quoteRefs.current[i] = el; }}
                className="text-foreground text-base leading-relaxed mb-8 font-medium"
                style={{ opacity: 0.3 }}
              >
                "{t.quote}"
              </blockquote>

              {/* Author */}
              <div data-component-id="src/app/components/TestimonialsSection.tsx:151:14" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="151" data-component-end-line="161" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%20pt-6%20border-t%20border-border%22%7D" className="flex items-center gap-4 pt-6 border-t border-border">
                <div data-component-id="src/app/components/TestimonialsSection.tsx:152:16" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="152" data-component-end-line="154" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-10%20h-10%20rounded-full%20bg-foreground%20flex%20items-center%20justify-center%20text-background%20text-xs%20font-bold%20flex-shrink-0%22%7D" className="w-10 h-10 rounded-full bg-foreground flex items-center justify-center text-background text-xs font-bold flex-shrink-0">
                  {t.initials}
                </div>
                <div data-component-id="src/app/components/TestimonialsSection.tsx:155:16" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="155" data-component-end-line="160" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <div data-component-id="src/app/components/TestimonialsSection.tsx:156:18" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="156" data-component-end-line="156" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-foreground%20font-semibold%20text-sm%22%7D" className="text-foreground font-semibold text-sm">{t.name}</div>
                  <div data-component-id="src/app/components/TestimonialsSection.tsx:157:18" data-component-path="src/app/components/TestimonialsSection.tsx" data-component-line="157" data-component-end-line="159" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-muted-foreground%20text-xs%22%2C%22textContent%22%3A%22%C2%B7%22%7D" className="text-muted-foreground text-xs">
                    {t.location} · {t.trek}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}